import styled from 'styled-components';

import CitationBadge from 'components/shared/ai/CitationBadge';
import { capitalize } from 'lib/util';
import { ComparisonAttachment, ComparisonContentType } from 'models/aiArtifacts/Comparison';
import colors from 'styles/theme/colors';

import Section from '../../../shared/ai/Section';

interface ComparisonContentProps {
  content: ComparisonContentType[];
  attachments: ComparisonAttachment[];
  pageByAttachmentId?: Record<string, number>;
}
function ComparisonContent({ content, attachments, pageByAttachmentId }: ComparisonContentProps) {
  return (
    <Container>
      {content.map((item, idx) =>
        ['changed', 'added', 'removed'].includes(item.text) ? (
          <SubsectionContainer key={`${item.text}-${idx}`}>
            <SubsectionHeader>
              <SubsectionTitle>{capitalize(item.text)}</SubsectionTitle>
              <CitationsContainer>
                {item?.attachmentIds?.flatMap((id) => {
                  const attachment = attachments.find(({ attachmentId }) => attachmentId === id);

                  return attachment ? (
                    <CitationBadge key={id} size={14} attachment={attachment} page={pageByAttachmentId?.[id]} />
                  ) : (
                    []
                  );
                })}
              </CitationsContainer>
            </SubsectionHeader>
            {item.children && <SubsectionContent content={item.children} />}
          </SubsectionContainer>
        ) : (
          <ComparisonContentItem key={`${item.text}-${idx}`}>
            {item.text}
            {item.children && <SubsectionContent content={item.children} />}
          </ComparisonContentItem>
        )
      )}
    </Container>
  );
}

function SubsectionContent({ content }: { content: ComparisonContentType[] }) {
  return (
    <ComparisonContentList>
      {content.map((item, idx) => (
        <ComparisonContentItem key={`${item.text}-${idx}`}>
          {item.text}
          {item.children && <SubsectionContent content={item.children} />}
        </ComparisonContentItem>
      ))}
    </ComparisonContentList>
  );
}

type ComparisonSectionProps = {
  title: string;
  attachments: ComparisonAttachment[];
  content: ComparisonContentType[];
  pageByAttachmentId?: Record<string, number>;
};

export default function ComparisonSection({ title, attachments, content, pageByAttachmentId }: ComparisonSectionProps) {
  return (
    <Section title={title} attachments={attachments} pageByAttachmentId={pageByAttachmentId}>
      <ComparisonContent content={content} attachments={attachments} pageByAttachmentId={pageByAttachmentId} />
    </Section>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ComparisonContentItem = styled.li`
  font-size: 12px;
  line-height: 22px;
  letter-spacing: 0.2px;
  color: ${colors.black};
`;

const ComparisonContentList = styled.ul`
  list-style: disc;
  padding-left: 20px;
`;

const SubsectionContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubsectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const CitationsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SubsectionTitle = styled.h3`
  font-size: 12px;
  line-height: 22px;
  letter-spacing: 0.2px;
  color: ${colors.black};
  margin: 0;
`;
